import * as React from 'react';
import { Modal, Typography, Divider, Table, Space, Card } from 'antd';
import { FileExcelOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

interface ExcelTemplateDocumentationProps {
  isVisible: boolean;
  onClose: () => void;
}

const ExcelTemplateDocumentation: React.FC<ExcelTemplateDocumentationProps> = ({
  isVisible,
  onClose,
}) => {
  // Define columns for the template structure table
  const columns = [
    {
      title: 'Column Name',
      dataIndex: 'column',
      key: 'column',
      width: '20%',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '35%',
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      width: '10%',
      render: (required: boolean) => (required ? 'Yes' : 'No'),
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: '10%',
    },
    {
      title: 'Allowed Values',
      dataIndex: 'allowedValues',
      key: 'allowedValues',
      width: '25%',
    },
  ];

  // Data for the template structure based on ReleasesExcel struct
  const data = [
    {
      key: '1',
      column: 'UPC/EAN',
      description: 'Universal Product Code or European Article Number for the release. If left empty, the system will automatically generate a UPC/EAN.',
      required: false,
      format: 'Text',
      allowedValues: '13 numerical digits (e.g., "0123456789012") or leave empty for auto-generation',
    },
    {
      key: '2',
      column: 'Version/Subtitle',
      description: 'Subtitle or version information for the release',
      required: false,
      format: 'Text',
    },
    {
      key: '3',
      column: 'Release Title',
      description: 'The title of the release',
      required: true,
      format: 'Text',
    },
    {
      key: '4',
      column: 'Main Artist(s)',
      description: 'Primary artists for the release (comma-separated for multiple artists)',
      required: true,
      format: 'Text',
      allowedValues: 'Single artist name or comma-separated list for multiple artists (e.g., "Artist1, Artist2")',
    },
    {
      key: '5',
      column: 'Featuring Artist(s)',
      description: 'Featured artists on the release (comma-separated for multiple artists)',
      required: false,
      format: 'Text',
      allowedValues: 'Single artist name or comma-separated list for multiple artists (e.g., "Artist1, Artist2")',
    },
    {
      key: '6',
      column: 'Original release date',
      description: 'Date when the release was originally published',
      required: false,
      format: 'Date',
      allowedValues: 'DD/MM/YYYY format (e.g., "01/12/2023" for December 1, 2023)',
    },
    {
      key: '7',
      column: 'Digital Release Date',
      description: 'Date when the release will be available digitally',
      required: true,
      format: 'Date',
      allowedValues: 'DD/MM/YYYY format (e.g., "01/12/2023" for December 1, 2023)',
    },
    {
      key: '8',
      column: 'Production Year',
      description: 'Year when the release was produced',
      required: true,
      format: 'Number',
      allowedValues: '4-digit year between 1925 and next year (e.g., "2023")',
    },
    {
      key: '9',
      column: 'Genre',
      description: 'Primary genre of the release',
      required: true,
      format: 'Text',
    },
    {
      key: '10',
      column: 'SubGenre',
      description: 'Secondary genre of the release',
      required: false,
      format: 'Text',
    },
    {
      key: '11',
      column: 'Label',
      description: 'Record label name',
      required: true,
      format: 'Text',
    },
    {
      key: '12',
      column: 'Asset Label',
      description: 'Parent organization of the label',
      required: false,
      format: 'Text',
    },
    {
      key: '13',
      column: 'Release Type',
      description: 'Type of release (Album, Single, EP, etc.)',
      required: true,
      format: 'Text',
      allowedValues: 'ALBUM, SINGLE, EP, COMPILATION',
    },
    {
      key: '14',
      column: 'Release Catalog number',
      description: 'Catalog number assigned by the label',
      required: false,
      format: 'Text',
    },
    {
      key: '15',
      column: 'Release Price Tier',
      description: 'Pricing tier for the release',
      required: false,
      format: 'Text',
      allowedValues: 'For SINGLE: DIGITAL45 (1,49€/1,99$). For EP: MINIEP, EP, MAXIEP, MAXIEP TWO, BUDGETONE, BUDGET, BACK. For ALBUM: BACK CATALOG, MIDLINE, MIDFRONT, MIDFRONT TWO, PREMIUM, DELUXE ONE, DELUXE TWO, DELUXE THREE, DELUXE FOUR.',
    },
    {
      key: '16',
      column: 'Track Number',
      description: 'Position of the track in the release',
      required: true,
      format: 'Number',
      allowedValues: 'Sequential numbers starting from 1 for each CD/volume',
    },
    {
      key: '17',
      column: 'Track Title',
      description: 'Title of the track',
      required: true,
      format: 'Text',
    },
    {
      key: '17a',
      column: 'Track Genre',
      description: 'Primary genre of this track. Leave empty to inherit the release Genre.',
      required: false,
      format: 'Text',
    },
    {
      key: '17b',
      column: 'Track SubGenre',
      description: 'Subgenre of this track. Leave empty to inherit the release SubGenre when Track Genre is also empty.',
      required: false,
      format: 'Text',
    },
    {
      key: '18',
      column: 'Track Main Artist(s)',
      description: 'Primary artists for the track (comma-separated for multiple artists)',
      required: true,
      format: 'Text',
      allowedValues: 'Single artist name or comma-separated list for multiple artists (e.g., "Artist1, Artist2")',
    },
    {
      key: '19',
      column: 'Track Featuring Artist(s)',
      description: 'Featured artists on the track (comma-separated for multiple artists)',
      required: false,
      format: 'Text',
      allowedValues: 'Single artist name or comma-separated list for multiple artists (e.g., "Artist1, Artist2")',
    },
    {
      key: '20',
      column: 'Authors',
      description: 'Authors of the track (comma-separated for multiple authors)',
      required: false,
      format: 'Text',
      allowedValues: 'Single author name or comma-separated list for multiple authors (e.g., "Author1, Author2")',
    },
    {
      key: '21',
      column: 'Composers',
      description: 'Composers of the track (comma-separated for multiple composers)',
      required: false,
      format: 'Text',
      allowedValues: 'Single composer name or comma-separated list for multiple composers (e.g., "Composer1, Composer2")',
    },
    {
      key: '22',
      column: 'Subtitle/Mixtitle/Version',
      description: 'Additional title information for the track',
      required: false,
      format: 'Text',
    },
    {
      key: '23',
      column: 'CD Number',
      description: 'Volume or CD number for multi-disc releases',
      required: false,
      format: 'Number',
      allowedValues: 'Positive integers (defaults to 1 if not specified)',
    },
    {
      key: '24',
      column: 'ISRC',
      description: 'International Standard Recording Code for the track. If left empty, the system will automatically generate an ISRC.',
      required: true,
      format: 'Text',
      allowedValues: '12 characters (e.g., "USRC12345678") or leave empty for auto-generation',
    },
    {
      key: '25',
      column: '℗ Line',
      description: 'Phonographic copyright information',
      required: false,
      format: 'Text',
    },
    {
      key: '26',
      column: '© Line',
      description: 'Copyright information',
      required: false,
      format: 'Text',
    },
    {
      key: '27',
      column: 'Tracktype',
      description: 'Type of track (Audio, Video, etc.)',
      required: false,
      format: 'Text',
      allowedValues: 'ORIGINAL, KARAOKE, MEDLEY, COVER, COVER BY BAND',
    },
    {
      key: '28',
      column: 'Lyrics Language',
      description: 'Language of the lyrics',
      required: false,
      format: 'Text',
      allowedValues: 'ISO language codes: en (English), es (Spanish), fr (French), de (German), it (Italian), pt (Portuguese), ja (Japanese), zh (Chinese), ru (Russian), ar (Arabic), and many more',
    },
    {
      key: '29',
      column: 'Title Language',
      description: 'Language of the track title',
      required: false,
      format: 'Text',
      allowedValues: 'ISO language codes: en (English), es (Spanish), fr (French), de (German), it (Italian), pt (Portuguese), ja (Japanese), zh (Chinese), ru (Russian), ar (Arabic), and many more',
    },
    {
      key: '30',
      column: 'Parental Advisory',
      description: 'Indicates if explicit content is present',
      required: false,
      format: 'Text',
      allowedValues: 'Yes (Explicit), No (Not Explicit)',
    },
    {
      key: '30a',
      column: 'Is Instrumental',
      description: 'Indicates if the track has no lyrics',
      required: false,
      format: 'Boolean',
      allowedValues: 'Yes (true), No (false)',
    },
    {
      key: '31',
      column: 'Territories to Deliver',
      description: 'Regions where the release should be distributed',
      required: false,
      format: 'Text',
      allowedValues: 'WW (Worldwide) or comma-separated ISO country codes (e.g., "US,GB,DE")',
    },
    {
      key: '32',
      column: 'Filename',
      description: 'Name of the audio file for the track',
      required: true,
      format: 'Text',
    },
    {
      key: '33',
      column: 'Cover Art Filename',
      description: 'Name of the image file for the release cover',
      required: true,
      format: 'Text',
    },
    {
      key: '34',
      column: 'Preview Start',
      description: 'Start time in seconds for the track preview',
      required: false,
      format: 'Number',
      allowedValues: 'Positive integer representing seconds from the start of the track (defaults to 45)',
    },
    {
      key: '35',
      column: 'Track Price Tier',
      description: 'Pricing tier for the individual track',
      required: false,
      format: 'Text',
      allowedValues: 'SINGLE1 (Back: 0.69€/0.69$/0.59£), SINGLE2 (Mid: 0.99€/0.99$/0.79£), SINGLE3 (Front: 1.29€/1.29$/0.99£)',
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#52c41a' }} />
          <span>Excel Template Documentation</span>
        </Space>
      }
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Typography>
        <Title level={4}>Overview</Title>
        <Paragraph>
          This Excel template is used for importing and updating music releases in the system. 
          It provides a structured format for entering release and track information that can be 
          processed by the system. The template follows a specific structure that matches the 
          ReleasesExcel format used by the backend processing system.
        </Paragraph>
        <Paragraph>
          The template includes fields for both release-level information (such as UPC/EAN, release title, 
          and release date) and track-level information (such as ISRC, track title, and artists). Each row 
          in the template represents a track, with release information repeated for all tracks in the same release.
        </Paragraph>

        <Divider />

        <Title level={4}>Template Structure</Title>
        <Paragraph>
          The template contains the following columns for entering release and track information:
        </Paragraph>

        <Table 
          columns={columns} 
          dataSource={data} 
          pagination={false} 
          bordered 
          size="small"
          style={{ marginBottom: '20px' }}
        />

        <Divider />

        <Title level={4}>Usage Instructions</Title>
        <Card style={{ marginBottom: '16px' }}>
          <Space direction="vertical">
            <Text strong>1. Download the template</Text>
            <Paragraph>
              Click the "Download Excel Template" button in the Releases table to get the latest version of the template.
            </Paragraph>

            <Text strong>2. Fill in the required information</Text>
            <Paragraph>
              Complete all required fields in the template. Make sure to follow the specified format for each field.
            </Paragraph>

            <Text strong>3. Import the completed template</Text>
            <Paragraph>
              Use the import functionality to upload your completed template to the system.
            </Paragraph>

            <Text strong>4. Verify the imported data</Text>
            <Paragraph>
              After import, verify that all your data has been correctly imported into the system.
            </Paragraph>
          </Space>
        </Card>

        <Divider />

        <Title level={4}>Tips and Best Practices</Title>
        <Paragraph>
          <ul>
            <li>Ensure all required fields are completed before importing</li>
            <li>Use the correct format for dates (DD/MM/YYYY) - for example, "01/12/2023" for December 1, 2023</li>
            <li>For multiple artists, authors, or composers, separate names with commas (e.g., "Artist1, Artist2")</li>
            <li>UPC/EAN codes must be valid and unique - typically 12 or 13 digits. Leave this field empty to have the system automatically generate a UPC/EAN.</li>
            <li>ISRC codes must be valid and unique - format should be 12 characters (e.g., "USRC12345678"). Leave this field empty to have the system automatically generate an ISRC.</li>
            <li>Track numbers should be sequential starting from 1 for each volume/CD</li>
            <li>CD Number (Volume Number) defaults to 1 if not specified</li>
            <li>Production Year should be a 4-digit year (e.g., "2023")</li>
            <li>Preview Start is measured in seconds from the beginning of the track</li>
            <li>Ensure filenames match the actual audio files and cover art files you plan to upload</li>
            <li>Save your work frequently while filling out the template</li>
          </ul>
        </Paragraph>

        <Divider />

        <Paragraph type="secondary">
          <InfoCircleOutlined /> Note: This documentation is based on the current version of the Excel template. 
          If you encounter any issues or have questions, please contact the system administrator.
        </Paragraph>
      </Typography>
    </Modal>
  );
};

export default ExcelTemplateDocumentation;
